import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TextStyle, SEOConfigItem, SEOConfig } from '../types';
import { BLOG_POSTS } from '../blogPosts';

/**
 * SEO Configuration for all pages
 * Title: 50-60 characters (with target keywords)
 * Description: 150-160 characters (with target keywords)
 */
const SEO_CONFIG: SEOConfig = {
  // === MAIN PAGES ===
  '/': {
    title: 'Fancy Text Generator \u1408 \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1 \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F Fonts Copy Paste', // 52cp/62u16
    description: 'Free fancy text generator with 60+ styles: \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1, \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F, \uD835\uDD09\uD835\uDD2F\uD835\uDD1E\uD835\uDD28\uD835\uDD31\uD835\uDD32\uD835\uDD2F & more. Instant copy paste for Instagram, TikTok, Discord. No signup needed.', // 142cp/159u16
    keywords: 'fancy letter generator, fancy text generator, font text, text fonts, fancy fonts, font generator, cool fonts, copy paste fonts, instagram fonts',
    type: 'WebApplication'
  },
  '/privacy': {
    title: 'Privacy Policy - FancyMyText Font Generator', // 44 chars
    description: 'Privacy Policy for FancyMyText font generator. Learn how we protect your data on our free font generator tool. Your text stays private, never stored.', // 155 chars
    keywords: 'privacy policy, fancymytext privacy, font generator privacy, data protection',
    type: 'WebPage'
  },
  '/terms': {
    title: 'Terms of Service - FancyMyText Font Generator', // 46 chars
    description: 'Terms of Service for FancyMyText font generator. Read the rules for using our free fancy font generator and text converter tool online.', // 148 chars
    keywords: 'terms of service, fancymytext terms, font generator terms, text generator terms',
    type: 'WebPage'
  },
  '/about': {
    title: 'About FancyMyText - Free Font Generator Tool Online', // 51 chars
    description: 'Learn about FancyMyText, a free font generator for creating fancy text styles. Generate cool fonts for Instagram, TikTok, Twitter and social media.', // 155 chars
    keywords: 'about fancymytext, font generator, fancy font generator, cool fonts, text generator, free font tool',
    type: 'WebPage'
  },
  '/app': {
    title: 'Install FancyMyText - Free Font Generator App Offline', // 53 chars
    description: 'Install FancyMyText font generator as a free app on iPhone, Android, or desktop. Works offline, privacy-first. Free font generator anywhere you go.', // 154 chars
    keywords: 'font generator app, pwa font generator, install fancy text, offline font generator, free text app',
    type: 'WebApplication'
  },

  // === PLATFORM-SPECIFIC PAGES ===
  '/instagram-fonts': {
    title: 'Instagram Fonts \u1408 \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1 \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F Bio & Caption Fonts', // 50cp/60u16
    description: 'Free Instagram fonts for bios, captions & stories. 60+ styles: \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1, \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F, \uD835\uDD09\uD835\uDD2F\uD835\uDD1E\uD835\uDD28\uD835\uDD31\uD835\uDD32\uD835\uDD2F. Copy paste Instagram fonts free.', // 129cp/146u16
    keywords: 'instagram fonts, font instagram, fonts instagram, instagram font generator, instagram bio fonts, ig fonts, cool instagram fonts',
    type: 'WebApplication'
  },
  '/discord-fonts': {
    title: 'Discord Font Generator - Username & Server Fonts Copy Paste', // 59 chars
    description: 'Free Discord font generator for usernames, nicknames & messages. Generate cool fonts for Discord servers. Copy paste Discord fonts online.', // 150 chars
    keywords: 'discord fonts, discord font generator, discord username fonts, discord server fonts, fonts for discord',
    type: 'WebApplication'
  },
  '/facebook-fonts': {
    title: 'Facebook Fonts - Facebook Font Changer Copy Paste Free', // 55 chars
    description: 'Free Facebook font changer for posts, bios & stylish FB names. Facebook font generator online. Copy paste Facebook fonts free. FB font style changer.', // 155 chars
    keywords: 'facebook fonts, facebook font changer, fb stylish names, font facebook, fb fonts, facebook font style, fonts facebook',
    type: 'WebApplication'
  },

  // === SEO KEYWORD LANDING PAGES ===
  '/bold-generator': {
    title: 'Bold Letter Generator - 12 𝐁𝐨𝐥𝐝 Letter Styles Copy Paste', // 56cp
    description: 'Free bold letter generator with 12 styles: 𝐬𝐞𝐫𝐢𝐟, 𝗦𝗮𝗻𝘀, 𝘽𝙤𝙡𝙙 𝙄𝙩𝙖𝙡𝙞𝙘, 𝕲𝖔𝖙𝖍𝖎𝖈 & more. Copy paste bold letters A-Z for Facebook, Instagram, X and TikTok.', // 150cp
    // Intent split vs /bold: this page owns "bold letters" (compare styles + A-Z alphabets);
    // /bold owns "bold text/font generator" (single style). Both self-canonical, cross-linked.
    keywords: 'bold letter generator, bold letters, bold letters copy paste, bold alphabet, bold letters for facebook, bold generator',
    type: 'WebApplication'
  },
  '/aesthetic-fonts': {
    title: 'Aesthetic Fonts Generator - Ａｅｓｔｈｅｔｉｃ Copy Paste Free', // 57 chars
    description: 'Free aesthetic font generator for Instagram, TikTok. Create vaporwave fonts and aesthetic text. Copy paste aesthetic fonts online.', // 143 chars
    keywords: 'aesthetic fonts, aesthetic font, fonts aesthetic, aesthetic font generator, vaporwave font, aesthetic text',
    type: 'WebApplication'
  },
  '/tiny-text': {
    title: 'Tiny Text Generator - ˢᵐᵃˡˡ ᶠᵒⁿᵗ Copy Paste Free Online', // 56 chars
    description: 'Free tiny text generator. Create small text, superscript, subscript fonts. Copy paste tiny letter generator online free.', // 132 chars
    keywords: 'tiny text generator, tiny font generator, tiny letter generator, small font, small text, subscript generator',
    type: 'WebApplication'
  },
  '/old-english': {
    title: 'Old English Font Generator - 𝔒𝔩𝔡 𝔈𝔫𝔤𝔩𝔦𝔰𝔥 Copy Paste', // 55 chars
    description: 'Free Old English font converter. Generate Gothic Fraktur blackletter fonts for Instagram, tattoos. Copy paste Old English fonts online.', // 149 chars
    keywords: 'old english converter, old english font, fraktur font, gothic font, blackletter font, medieval font generator',
    type: 'WebApplication'
  },
  '/cool-fonts': {
    title: 'Cool Font Generator - Cool Letter Generator Copy Paste', // 54 chars
    description: 'Free cool font generator and cool letter generator with fancy font styles. Generate cool fonts for Instagram, TikTok, Discord. Copy paste free online.', // 155 chars
    keywords: 'cool font generator, cool letter generator, fancy font style, cool fonts, stylish fonts, cool fonts online, cool lettering',
    type: 'WebApplication'
  },
  '/stylish-name-generator': {
    title: 'Stylish Name Generator - Make Stylish Name Free Online', // 55 chars
    description: 'Free stylish name maker for PUBG, Free Fire, Instagram, Facebook. Generate stylish nicknames and gaming names. Copy paste stylish names.', // 149 chars
    keywords: 'stylish name generator, nickname stylish, make stylish name, stylish name maker, fb stylish names, pubg name generator, stylish names generator',
    type: 'WebApplication'
  },
  '/jawa-translator': {
    title: 'Jawa Translator - Jawaese Star Wars Language Translator', // 55 chars
    description: 'Free Jawa translator for Star Wars Jawaese language. Translate English to Jawaese. Utinni! Learn Jawa words from Tatooine. Copy paste Jawa text.', // 155 chars
    keywords: 'jawa translator, jawaese translator, translate jawa, star wars jawa language, jawa language, utinni, tatooine language',
    type: 'WebApplication'
  },
  '/number-to-words': {
    title: 'Number to Words Converter - Spell Out Numbers Free Online', // 57 chars
    description: 'Free number to words converter. Convert numbers to words and words to numbers instantly. Currency, ordinal, decimal support. Spell out numbers online.', // 158 chars
    keywords: 'number to words, words to number, number to word converter, spell out numbers, digit to word, numbers in words, convert numbers to words',
    type: 'WebApplication'
  },
  '/fancy-letter-generator': {
    title: 'Fancy Letter Generator - Beautiful Fonts Copy Paste Free', // 56 chars
    description: 'Free fancy letter generator with cursive, gothic, bubble fonts. Create fancy letters for Instagram, TikTok, Facebook. Copy paste fancy fonts online.', // 155 chars
    keywords: 'fancy letter generator, fancy letters, fancy font generator, fancy text generator, beautiful fonts, decorative letters',
    type: 'WebApplication'
  },
  '/stylish-text': {
    title: 'Stylish Text Generator - Generate Stylish Text Copy Paste', // 58 chars
    description: 'Free stylish text generator to generate stylish text for Instagram, TikTok, Twitter. Stylish fonts and stylish text creator. Copy paste free online.', // 155 chars
    keywords: 'stylish text, generate stylish text, stylish text generator, stylish fonts, stylish text creator, stylish font generator',
    type: 'WebApplication'
  },
  '/cute-name-generator': {
    title: 'Cute Name Generator - Kawaii Names Copy Paste Free', // 51 chars
    description: 'Free cute name generator with kawaii fonts and decorations. Create cute names for Instagram, TikTok, Discord, gaming. Copy paste cute names online.', // 155 chars
    keywords: 'cute name generator, cute names, kawaii names, cute font generator, adorable names, cute username generator',
    type: 'WebApplication'
  },
  '/fancy-numbers': {
    title: 'Fancy Numbers Generator - Bold Circled Numbers Copy Paste', // 57 chars
    description: 'Free fancy numbers generator. Convert numbers to bold, circled, subscript, superscript styles. Copy paste fancy number fonts online free.', // 149 chars
    keywords: 'fancy numbers, fancy number generator, bold numbers, circled numbers, subscript numbers, superscript numbers, number fonts',
    type: 'WebApplication'
  },
  '/scifi-fonts': {
    title: 'Sci-Fi Font Generator - Runic Alien Matrix Fonts Free', // 54 chars
    description: 'Free sci-fi font generator with runic, alien, matrix, cyberpunk styles. Generate futuristic fonts for gaming, Discord. Copy paste sci-fi fonts.', // 154 chars
    keywords: 'scifi fonts, sci-fi font generator, runic font, alien font, matrix font, cyberpunk font, futuristic fonts, gaming fonts',
    type: 'WebApplication'
  },

  // === MULTILINGUAL PAGES ===
  '/es': {
    title: 'Letras Bonitas para Copiar y Pegar ✿ 𝓛𝓮𝓽𝓻𝓪𝓼 Generador', // 53cp
    description: 'Letras bonitas para copiar y pegar gratis: 𝓬𝓾𝓻𝓼𝓲𝓿𝓪𝓼, 𝗻𝗲𝗴𝗿𝗶𝘁𝗮𝘀, ⓑⓤⓡⓑⓤⓙⓐⓢ y 60+ estilos. Generador de letras para Instagram, TikTok y WhatsApp.', // 141cp
    keywords: 'generador de letras, letras bonitas, letras para instagram, letras cursivas, letras diferentes, letras para copiar y pegar, letra bonita',
    type: 'WebApplication'
  },
  '/pt': {
    title: 'Fontes para Instagram - Gerador de Letras Bonitas Gratis', // 57 chars
    description: 'Fontes para Instagram gratis. Gerador de fontes para bio, legendas e stories do Instagram. Letras bonitas para copiar e colar. Fontes TikTok.', // 153 chars
    keywords: 'fontes para instagram, gerador de fontes, fontes instagram, letras bonitas, fontes de letras, bio instagram, legendas instagram, fontes para copiar',
    type: 'WebApplication'
  },
  '/fr': {
    title: 'Générateur de Polices - Belles Lettres Copier Coller', // 53 chars
    description: 'Générateur de polices gratuit pour Instagram, TikTok et réseaux sociaux. Créez de belles polices cursives et différentes. Copier coller.', // 151 chars
    keywords: 'générateur de polices, belles lettres, polices instagram, polices cursives, copier coller polices, générateur texte',
    type: 'WebApplication'
  },
  '/id': {
    title: 'Generator Font Keren - Huruf Keren Salin Tempel Gratis', // 54 chars
    description: 'Generator font gratis untuk Instagram, TikTok dan media sosial. Buat huruf keren, font aesthetic. Salin dan tempel font keren online.', // 148 chars
    keywords: 'generator font, font keren, huruf keren, font aesthetic, font instagram, salin tempel font, tulisan keren',
    type: 'WebApplication'
  },
  '/ph': {
    title: 'Font Style Generator ✨ Magagandang 𝓕𝓸𝓷𝓽 Style Copy Paste', // 56cp
    description: 'Libreng font style generator: gawing 𝓬𝓾𝓻𝓼𝓲𝓿𝓮, 𝗯𝗼𝗹𝗱 at ⓑⓤⓑⓑⓛⓔ ang text mo. 60+ font style para sa Facebook, TikTok, Instagram at ML. I-copy paste agad.', // 150cp
    keywords: 'font style, font style generator, font generator, magagandang font, fonts para sa instagram, aesthetic font, copy paste fonts, fancy text',
    type: 'WebApplication'
  },
  '/jp': {
    title: 'フォントジェネレーター - 特殊文字 コピペ 無料', // 25 chars (Japanese is compact)
    description: '無料のフォントジェネレーター。Instagram、TikTok、Twitter用の特殊文字・可愛い文字を作成。60種類以上のおしゃれなフォントをコピペで簡単に使えます。SNS映えする文字変換ツール。', // 120+ chars
    keywords: 'フォントジェネレーター, 特殊文字, コピペ, 可愛い文字, おしゃれ フォント, インスタ 文字',
    type: 'WebApplication'
  },
  '/kr': {
    title: '글꼴 생성기 - 예쁜 글씨 복사 붙여넣기 무료', // Korean title
    description: '무료 글꼴 생성기로 인스타그램, 틱톡, 트위터용 특수문자와 예쁜 글씨를 만들어보세요. 60가지 이상의 멋진 폰트 스타일을 복사 붙여넣기로 쉽게 사용할 수 있습니다. 온라인 무료 서비스.', // 120+ chars
    keywords: '글꼴 생성기, 예쁜 글씨, 특수문자, 폰트 생성기, 인스타 글씨, 복사 붙여넣기',
    type: 'WebApplication'
  },
  '/cn': {
    title: '字体生成器 - 特殊字体 复制粘贴 免费在线', // Chinese title
    description: '免费在线字体生成器，为Instagram、TikTok、微博、微信创建60多种特殊字体和艺术字。复制粘贴即可使用的花样字体、个性网名字体生成工具，让你的社交媒体更出众。', // 120+ chars
    keywords: '字体生成器, 特殊字体, 艺术字, 花样字体, 复制粘贴, 网名字体',
    type: 'WebApplication'
  },
  '/de': {
    title: 'Schriftgenerator - Schöne Schriften Kopieren Einfügen', // 53 chars (German)
    description: 'Kostenloser Schriftgenerator mit 60+ Schriftstilen. Frakturschrift, gotische Schrift für Instagram, TikTok. Schöne Schriften kopieren und einfügen.', // 155 chars
    keywords: 'schriftgenerator, frakturschrift generator, schöne schriften, gotische schrift, schriften kopieren, schrift generator',
    type: 'WebApplication'
  },
  '/th': {
    title: 'ฟอนต์สวยๆ - เครื่องมือสร้างฟอนต์ คัดลอกและวาง ฟรี', // Thai title
    description: 'เครื่องมือสร้างฟอนต์ฟรี 60+ สไตล์ สำหรับ Instagram, TikTok, Facebook ฟอนต์สวยๆ ตัวอักษรสวยๆ คัดลอกและวางได้ทันที ใช้ได้ฟรีไม่ต้องลงทะเบียน', // 155 chars
    keywords: 'ฟอนต์สวยๆ, ตัวอักษรสวยๆ, เครื่องมือสร้างฟอนต์, font สวยๆ, แปลงตัวอักษร, คัดลอกและวาง',
    type: 'WebApplication'
  },
  '/vi': {
    title: 'Font Chữ Đẹp - Công Cụ Tạo Font Sao Chép Dán Miễn Phí', // Vietnamese title
    description: 'Công cụ tạo font chữ miễn phí 60+ kiểu cho Instagram, TikTok, Facebook. Font chữ đẹp, chữ nghệ thuật, sao chép và dán dễ dàng. Hoàn toàn miễn phí.', // 155 chars
    keywords: 'font chữ đẹp, chữ nghệ thuật, tạo font chữ, font instagram, chữ đẹp copy paste, kiểu chữ đẹp',
    type: 'WebApplication'
  },

  // === TOOLS ===
  '/contact': {
    title: 'Contact Us - FancyMyText Font Generator Support', // 48 chars
    description: 'Contact FancyMyText font generator team. Get help, report bugs, or suggest new features. We respond within 24-48 hours.', // 130 chars
    keywords: 'contact fancymytext, font generator support, contact font generator',
    type: 'WebPage'
  },
  '/text-analyzer': {
    title: 'Character Counter & Word Counter - Free Text Analyzer', // 54 chars
    description: 'Free character counter and word counter tool. Count characters, words, sentences, paragraphs. Reading time calculator. Text analyzer online.', // 152 chars
    keywords: 'character counter, word counter, text analyzer, letter counter, character count, word count',
    type: 'WebApplication'
  },
  '/case-converter': {
    title: 'Text Case Converter - UPPERCASE lowercase Title Case', // 52 chars
    description: 'Free text case converter. Change text to UPPERCASE, lowercase, Title Case, Sentence case. Convert text capitalization online free.', // 143 chars
    keywords: 'case converter, text case converter, uppercase converter, lowercase converter, title case, change text case',
    type: 'WebApplication'
  },
  '/symbols': {
    title: 'Copy Paste Symbols - 2800+ Hearts Stars Arrows Emoji', // 53 chars
    description: 'Copy paste 2800+ symbols and emojis. Hearts, stars, arrows, faces, animals, flags for Instagram, Twitter, Discord. Click to copy free.', // 149 chars
    keywords: 'copy paste symbols, special characters, heart symbols, star symbols, arrow symbols, emoji copy paste, all emojis',
    type: 'WebApplication'
  },
  '/blog': {
    title: 'Fancy Text Blog - Tips for Social Media Fonts', // 46 chars
    description: 'Tips and guides for using fancy fonts on Instagram, Discord, TikTok. Learn about Unicode fonts, best styles, and how to use them effectively.', // 153 chars
    keywords: 'fancy text blog, font generator tips, instagram fonts guide, discord fonts guide, unicode fonts',
    type: 'WebPage'
  },
  '/faq': {
    title: 'FAQ - Frequently Asked Questions About Fancy Text', // 49 chars
    description: 'Answers to common questions about Unicode fonts, platform compatibility, privacy, and how to use fancy text on Instagram, Discord, TikTok.', // 151 chars
    keywords: 'fancy text faq, unicode fonts questions, font generator help, text generator faq',
    type: 'WebPage'
  },
  '/compatibility': {
    title: 'Platform Compatibility Guide - Which Fonts Work Where', // 53 chars
    description: 'See which fancy text styles work on Instagram, Discord, Twitter, Facebook. Complete Unicode compatibility matrix for all platforms.', // 143 chars
    keywords: 'unicode compatibility, fancy text platforms, font compatibility, instagram fonts support',
    type: 'WebPage'
  },

  // === BLOG POSTS ===
  '/blog/how-to-use-fancy-fonts-instagram': {
    title: 'Fancy Fonts for Instagram Bio - Step-by-Step Guide', // 50cp
    description: 'Learn how to add fancy fonts to your Instagram bio, name, captions and stories. Step-by-step guide with the best font picks and copy paste examples that work.', // 158cp
    keywords: 'instagram bio fonts, fancy fonts instagram, instagram font generator, instagram bio text styles',
    type: 'BlogPosting'
  },
  '/blog/best-discord-fonts-username': {
    title: 'Best Discord Fonts for Display Names, Servers & Chat', // 52cp
    description: 'The best fonts for your Discord display name, server nickname, server and channel names. Why @usernames reject them, plus copy paste Unicode font examples.', // 155cp
    keywords: 'discord fonts username, discord name fonts, fancy discord text, discord server fonts',
    type: 'BlogPosting'
  },
  '/blog/what-are-unicode-fonts': {
    title: 'What Are Unicode Fonts? How Fancy Text Really Works', // 51cp
    description: 'Understand the technology behind fancy text generators: what Unicode is, how styled letters like 𝗯𝗼𝗹𝗱 and 𝓬𝓾𝓻𝓼𝓲𝓿𝓮 work, and why they paste almost everywhere.', // 157cp
    keywords: 'unicode fonts, what is unicode, how unicode fonts work, fancy text technology, unicode characters',
    type: 'BlogPosting'
  },
  '/blog/top-10-fancy-text-styles': {
    title: 'Top 10 Fancy Text Styles for Social Media in 2026 (Copy)', // 56cp
    description: 'The 10 most popular fancy text styles for Instagram, TikTok and X, with examples of each style and when to use them. Copy paste any style free in one click.', // 156cp
    keywords: 'fancy text styles, best text styles social media, popular unicode fonts, top font styles',
    type: 'BlogPosting'
  },
  '/blog/instagram-vs-discord-fonts': {
    title: 'Instagram Fonts vs Discord Fonts - Key Differences', // 50cp
    description: 'Compare fancy fonts on Instagram vs Discord: which Unicode styles display well on each platform, where names and bios differ, and how to pick the right font.', // 157cp
    keywords: 'instagram vs discord fonts, font comparison, platform fonts, unicode fonts comparison',
    type: 'BlogPosting'
  },
  '/blog/tiktok-display-name-fonts': {
    title: 'Fancy Fonts for TikTok Display Name - Copy Paste Guide', // 54cp
    description: 'Add fancy fonts to your TikTok display name with this step-by-step guide. See which Unicode fonts work, the name limits, and username vs display name rules.', // 156cp
    keywords: 'tiktok display name fonts, fancy fonts tiktok, tiktok name generator, tiktok bio fonts',
    type: 'BlogPosting'
  },
  '/blog/threads-app-fonts': {
    title: 'Threads App Fonts - Stand Out on Meta\'s Threads Platform', // 56cp
    description: 'Use fancy text on Threads by Meta. Learn which Unicode fonts work in your Threads bio, name and posts, with copy paste styles to make your profile stand out.', // 157cp
    keywords: 'threads app fonts, threads bio fonts, meta threads fonts, fancy text threads',
    type: 'BlogPosting'
  },
  '/blog/zalgo-text-halloween': {
    title: 'Zalgo Text for Halloween - Creepy Glitch Font Generator', // 55cp
    description: 'Create spooky Zalgo text for Halloween posts and horror content. Copy paste creepy glitch text for creepypasta, gaming, Discord and social media captions.', // 154cp
    keywords: 'zalgo text halloween, glitch text generator, creepy text, halloween fonts, horror text',
    type: 'BlogPosting'
  },
  '/blog/free-fire-stylish-name-generator': {
    title: 'Free Fire Stylish Name Generator - Pro Nickname Ideas', // 53cp
    description: 'Create unique Free Fire names with fancy fonts and symbols. See popular characters, how to test them in the name editor, and stylish nickname ideas to copy.', // 156cp
    keywords: 'free fire stylish name generator, ff name generator, free fire nickname, garena free fire names',
    type: 'BlogPosting'
  },
  '/blog/pubg-name-symbols-that-work': {
    title: 'PUBG Mobile Name Symbols That Work in 2026 - Copy Paste', // 55cp
    description: 'Updated 2026 list of popular PUBG Mobile name symbols and fonts. Copy paste characters players use for clan tags and IGNs, and how to test before renaming.', // 155cp
    keywords: 'pubg name symbols, pubg mobile name generator, pubg symbols that work, pubg username',
    type: 'BlogPosting'
  },
  '/blog/aesthetic-symbols-bio': {
    title: 'Aesthetic Symbols for Bios - Copy Paste Collection', // 50cp
    description: 'Curated aesthetic symbols for Instagram, TikTok and X bios: coquette, soft, minimalist and kawaii symbols, plus ready-made bio templates to copy and paste.', // 155cp
    keywords: 'aesthetic symbols copy paste, bio symbols, aesthetic bio, kawaii symbols, soft aesthetic',
    type: 'BlogPosting'
  },
  '/blog/strikethrough-text-social-media': {
    title: 'Strikethrough Text for Social Media - Works Everywhere', // 54cp
    description: 'Create s̶t̶r̶i̶k̶e̶t̶h̶r̶o̶u̶g̶h̶ text for Instagram, Facebook, TikTok and X. Unicode strikethrough works where there is no native formatting. Copy paste free.', // 159cp
    keywords: 'strikethrough text generator, crossed out text, strikethrough social media, unicode strikethrough',
    type: 'BlogPosting'
  },
  '/blog/bubble-text-generator': {
    title: 'Bubble Text Generator Guide - Where and How to Use', // 50cp
    description: 'Create Ⓑⓤⓑⓑⓛⓔ letters with our free bubble text generator. Learn where bubble text works and creative ways to use circled letters on social media and in chat.', // 158cp
    keywords: 'bubble text generator, bubble letters, circled text, enclosed letters copy paste',
    type: 'BlogPosting'
  },
  '/blog/upside-down-text-generator': {
    title: 'Upside Down Text Generator - ʇxǝʇ uʍop ǝpᴉsdn Guide', // 51cp
    description: 'Create ʇxǝʇ uʍop ǝpᴉsdn for fun messages and pranks. Learn how flipped text works, which letters flip, and where to paste it on social media and in chat apps.', // 158cp
    keywords: 'upside down text generator, flip text, flipped text, inverted text generator',
    type: 'BlogPosting'
  },
  '/blog/small-text-generator': {
    title: 'Small Text Generator - Subscript & Superscript Guide', // 52cp
    description: 'Create ᵗⁱⁿʸ text and small caps for aesthetic bios. Learn how superscript, subscript and mini letters work, and which platforms display them correctly.', // 151cp
    keywords: 'small text generator, tiny text, superscript generator, subscript text, small caps',
    type: 'BlogPosting'
  },
  '/blog/cursive-fonts-whatsapp': {
    title: 'Cursive Fonts for WhatsApp Status & Messages Guide', // 50cp
    description: 'Add elegant 𝓬𝓾𝓻𝓼𝓲𝓿𝓮 text to your WhatsApp status, messages and profile name. Copy paste fancy fonts that work in WhatsApp, plus its built-in formatting tricks.', // 159cp
    keywords: 'cursive fonts whatsapp, whatsapp status fonts, whatsapp fancy text, cursive text',
    type: 'BlogPosting'
  },
  '/blog/gothic-font-generator': {
    title: 'Gothic Font Generator - Fraktur for Metal Aesthetics', // 52cp
    description: 'Create 𝔤𝔬𝔱𝔥𝔦𝔠 text with our Fraktur font generator. Copy paste blackletter fonts for metal bands, dark aesthetics, tattoos and alternative style social content.', // 160cp
    keywords: 'gothic font generator, fraktur font, blackletter font, metal fonts, dark aesthetic fonts',
    type: 'BlogPosting'
  },
  '/blog/vaporwave-text-generator': {
    title: 'Vaporwave Text Generator - Aesthetic Wide Text Guide', // 52cp
    description: 'Generate vaporwave Ａｅｓｔｈｅｔｉｃ text with fullwidth characters. Learn about vaporwave culture and how to use wide text effectively in your bios and captions.', // 154cp
    keywords: 'vaporwave text generator, aesthetic text, fullwidth text, wide text, retrowave fonts',
    type: 'BlogPosting'
  },
  '/blog/character-counter-social-media': {
    title: 'Character Counter for Social Media - Platform Limits Guide', // 58cp
    description: 'Character limits for X, Instagram, TikTok and more, and how Unicode fancy fonts and emoji affect your count. Check any post with our free character counter.', // 156cp
    keywords: 'character counter social media, twitter character limit, instagram bio limit, tiktok caption limit',
    type: 'BlogPosting'
  },
  '/blog/bold-text-facebook-posts': {
    title: 'Bold Text for Facebook Posts - No Markdown Needed (Copy)', // 56cp
    description: 'Create 𝗯𝗼𝗹𝗱 text for Facebook posts, comments and Marketplace listings. Unicode bold works where Facebook has no formatting buttons. Copy paste it free.', // 152cp
    keywords: 'bold text facebook, facebook bold letters, facebook post formatting, unicode bold',
    type: 'BlogPosting'
  },
  '/blog/gaming-clan-name-symbols': {
    title: 'Gaming Clan Names with Special Symbols - Copy Paste Guide', // 57cp
    description: 'Create gaming clan tags with decorative symbols like ꧁꧂ and 【】. Copy paste ideas for esports teams, Discord servers and gaming crews, and games that allow them.', // 160cp
    keywords: 'gaming clan name symbols, clan tag generator, esports team names, discord server names',
    type: 'BlogPosting'
  },
  '/blog/number-to-words-guide': {
    title: 'Number to Words Converter Guide - When You Need It', // 50cp
    description: 'Convert numbers to words for checks, legal documents and school work. Learn when to spell out numbers, the writing rules, and use our free converter tool.', // 154cp
    keywords: 'number to words converter, spell out numbers, check writing numbers, numbers in words',
    type: 'BlogPosting'
  },
  '/blog/twitter-bio-fonts': {
    title: 'Twitter/X Bio Fonts - Make Your X Profile Stand Out (Copy)', // 58cp
    description: 'Customize your X (Twitter) profile with fancy fonts. Learn which Unicode styles work in your bio, display name and posts, with copy paste examples for each.', // 156cp
    keywords: 'twitter bio fonts, x bio fonts, twitter display name fonts, twitter profile fonts',
    type: 'BlogPosting'
  },
  '/blog/case-converter-guide': {
    title: 'Case Converter Guide - Uppercase, Lowercase, Title', // 50cp
    description: 'Complete guide to text case conversion: when to use UPPERCASE, lowercase, Title Case, Sentence case and more. Convert any text instantly with our free tool.', // 156cp
    keywords: 'case converter guide, uppercase lowercase, title case converter, text case',
    type: 'BlogPosting'
  },
  '/blog/fortnite-name-fonts': {
    title: 'Fortnite Name Ideas - Cool Fonts & Symbols for Usernames', // 56cp
    description: 'Create unique Fortnite names with fancy fonts and symbols. Epic Games display name rules explained, which special characters work, and name ideas to copy.', // 154cp
    keywords: 'fortnite name generator, fortnite username fonts, epic games name, fortnite symbols',
    type: 'BlogPosting'
  },
  '/blog/aesthetic-text-social-media-2026': {
    title: 'Aesthetic Text for Social Media 2026 - Style Guide', // 50cp
    description: 'Master aesthetic text for Instagram, TikTok and Pinterest. Trending styles, vaporwave fonts, and tips for creating visually stunning social media text in 2026.', // 159cp
    keywords: 'aesthetic text, aesthetic fonts, vaporwave text, social media fonts 2026, aesthetic instagram',
    type: 'BlogPosting'
  },
  '/blog/tiktok-bio-fonts-guide-2026': {
    title: 'TikTok Bio Fonts Guide 2026 - Best Styles, Limits & Tips', // 56cp
    description: 'Complete TikTok bio fonts guide: which fancy fonts work, character limits, and how to make your TikTok profile stand out with stylish text. Copy paste free.', // 156cp
    keywords: 'tiktok bio fonts, tiktok fonts, tiktok username fonts, tiktok bio ideas, fancy text tiktok',
    type: 'BlogPosting'
  },
  '/blog/gaming-username-fonts-2026': {
    title: 'Gaming Username Fonts 2026 - Fortnite, Valorant, PUBG', // 53cp
    description: 'Create the ultimate gaming username with fancy fonts. Guide for Fortnite, Valorant, PUBG and more, with trending styles, symbols and clan tags to copy paste.', // 157cp
    keywords: 'gaming fonts 2026, gaming username generator, valorant name fonts, fortnite fonts, pubg stylish name',
    type: 'BlogPosting'
  },
  '/blog/unicode-fonts-platform-compatibility-2026': {
    title: 'Unicode Fonts Compatibility Guide 2026 - All Platforms', // 54cp
    description: 'Complete Unicode fonts compatibility guide: which fancy fonts work on Instagram, TikTok, Discord, X, Facebook, WhatsApp and more, tested across devices.', // 152cp
    keywords: 'unicode fonts compatibility, fancy fonts instagram, discord fonts, platform font support',
    type: 'BlogPosting'
  },

  // === BASIC STYLES ===
  '/bold': {
    title: 'Bold Font Generator - 𝗕𝗼𝗹𝗱 Text Copy Paste Free Online', // 56 chars
    description: 'Free bold font generator for Instagram, TikTok, Twitter. Generate 𝗕𝗼𝗹𝗱 fonts instantly with our font generator. Copy paste bold text anywhere.', // 155 chars
    keywords: 'bold font generator, bold text generator, bold font, bold text copy paste, texto en negrita',
    type: 'WebApplication'
  },
  '/bold-serif': {
    title: 'Bold Serif Font Generator - 𝐁𝐨𝐥𝐝 Classic Fonts Free', // 54 chars
    description: 'Free bold serif font generator. Create classic 𝐁𝐨𝐥𝐝 𝐒𝐞𝐫𝐢𝐟 fonts for Instagram, TikTok bios and posts. Copy paste font generator online.', // 154 chars
    keywords: 'bold serif font generator, classic bold font, serif bold font generator',
    type: 'WebApplication'
  },
  '/italic': {
    title: 'Italic Text Generator - 𝘐𝘵𝘢𝘭𝘪𝘤𝘴 Font Copy and Paste', // 51cp
    description: 'Free italic text generator: turn words into 𝘪𝘵𝘢𝘭𝘪𝘤𝘴 to copy and paste on Instagram, X, Facebook and Discord. Serif, sans-serif and bold italic font styles.', // 155cp
    keywords: 'italic text generator, italics copy and paste, italics generator, italic font generator, italic font copy paste',
    type: 'WebApplication'
  },
  '/italic-serif': {
    title: 'Italic Serif Font Generator - 𝐼𝑡𝑎𝑙𝑖𝑐 Elegant Fonts', // 54 chars
    description: 'Free italic serif font generator. Create elegant 𝐼𝑡𝑎𝑙𝑖𝑐 𝑆𝑒𝑟𝑖𝑓 fonts for Instagram, Facebook, Twitter. Copy paste font generator online.', // 152 chars
    keywords: 'italic serif font generator, classic italic font generator, elegant italic font',
    type: 'WebApplication'
  },
  '/bold-italic': {
    title: 'Bold Italic Font Generator - 𝘽𝙤𝙡𝙙 𝙄𝙩𝙖𝙡𝙞𝙘 Fonts Free', // 56 chars
    description: 'Free bold italic font generator. Generate 𝘽𝙤𝙡𝙙 𝙄𝙩𝙖𝙡𝙞𝙘 fonts for maximum emphasis on social media. Copy paste font generator online.', // 151 chars
    keywords: 'bold italic font generator, bold italic font, emphasis font generator',
    type: 'WebApplication'
  },
  '/bold-italic-serif': {
    title: 'Bold Italic Serif Font Generator - 𝑩𝒐𝒍𝒅 Classic Fonts', // 55 chars
    description: 'Free bold italic serif font generator. Generate elegant 𝑩𝒐𝒍𝒅 𝑰𝒕𝒂𝒍𝒊𝒄 fonts for professional social media posts. Copy paste font generator.', // 154 chars
    keywords: 'bold italic serif font generator, classic bold italic font generator',
    type: 'WebApplication'
  },
  '/monospace': {
    title: 'Monospace Font Generator - 𝙼𝚘𝚗𝚘 Typewriter Fonts Free', // 55 chars
    description: 'Free monospace font generator. Create typewriter style 𝙼𝚘𝚗𝚘𝚜𝚙𝚊𝚌𝚎 fonts for Instagram, Twitter, Discord. Copy paste font generator online.', // 155 chars
    keywords: 'monospace font generator, typewriter font generator, code font generator',
    type: 'WebApplication'
  },
  '/small-caps': {
    title: 'Small Caps Font - ꜱᴍᴀʟʟ ᴄᴀᴘꜱ Generator Copy Paste Free', // 56 chars
    description: 'Free small caps font generator. Create ꜱᴍᴀʟʟ ᴄᴀᴘꜱ text for Instagram, TikTok, Discord. Small capital letters font style copy paste online.', // 155 chars
    keywords: 'small caps font, small caps font generator, small capital font, small caps text, small uppercase font, smallcaps font',
    type: 'WebApplication'
  },
  '/superscript': {
    title: 'Superscript Font Generator - ˢᵘᵖᵉʳˢᶜʳⁱᵖᵗ Tiny Fonts', // 54 chars
    description: 'Free superscript font generator. Create tiny ˢᵘᵖᵉʳˢᶜʳⁱᵖᵗ fonts for Instagram, Twitter, Facebook. Copy paste small font generator online.', // 152 chars
    keywords: 'superscript font generator, tiny font generator, small text font generator',
    type: 'WebApplication'
  },
  '/subscript': {
    title: 'Subscript Font Generator - ₛᵤᵦₛ꜀ᵣᵢₚₜ Small Fonts Free', // 55 chars
    description: 'Free subscript font generator. Generate ₛᵤᵦₛ꜀ᵣᵢₚₜ fonts for formulas and notes. Copy paste font generator for Instagram, Twitter online.', // 152 chars
    keywords: 'subscript font generator, lowered font generator, chemical formula font',
    type: 'WebApplication'
  },

  // === DECORATIVE STYLES ===
  '/cursive': {
    title: 'Cursive Font Generator - 𝓒𝓾𝓻𝓼𝓲𝓿𝓮 Text Copy Paste Free', // 56 chars
    description: 'Free cursive font generator for Instagram & TikTok. Generate beautiful 𝓒𝓾𝓻𝓼𝓲𝓿𝓮 script fonts. Copy paste cursive font generator online.', // 152 chars
    keywords: 'cursive font generator, script font generator, letras cursivas, fancy cursive font',
    type: 'WebApplication'
  },
  '/cursive-light': {
    title: 'Cursive Light Font Generator - 𝒞𝓊𝓇𝓈𝒾𝓋𝑒 Elegant Fonts', // 56 chars
    description: 'Free light cursive font generator. Create elegant 𝒞𝓊𝓇𝓈𝒾𝓋𝑒 script fonts for Instagram bios and posts. Copy paste font generator online.', // 153 chars
    keywords: 'light cursive font generator, elegant script font generator, delicate cursive font',
    type: 'WebApplication'
  },
  '/fraktur': {
    title: 'Fraktur Font Generator - 𝔉𝔯𝔞𝔨𝔱𝔲𝔯 Gothic Fonts Free', // 53 chars
    description: 'Free fraktur font generator. Generate gothic 𝔉𝔯𝔞𝔨𝔱𝔲𝔯 blackletter fonts for Instagram, metal aesthetics. Copy paste font generator online.', // 155 chars
    keywords: 'fraktur font generator, gothic font generator, blackletter font generator, medieval font',
    type: 'WebApplication'
  },
  '/bold-fraktur': {
    title: 'Bold Fraktur Font Generator - 𝕭𝖔𝖑𝖉 Gothic Fonts Free', // 54 chars
    description: 'Free bold fraktur font generator. Generate heavy 𝕭𝖔𝖑𝖉 𝕱𝖗𝖆𝖐𝖙𝖚𝖗 blackletter fonts for metal aesthetics. Copy paste font generator online.', // 155 chars
    keywords: 'bold fraktur font generator, gothic font generator, heavy blackletter font',
    type: 'WebApplication'
  },
  '/double-struck': {
    title: 'Double Struck Font Generator - 𝔻𝕠𝕦𝕓𝕝𝕖 Outlined Fonts', // 56 chars
    description: 'Free double struck font generator. Create outlined 𝔻𝕠𝕦𝕓𝕝𝕖 𝕊𝕥𝕣𝕦𝕔𝕜 fonts for math aesthetics. Copy paste font generator online free.', // 152 chars
    keywords: 'double struck font generator, outlined font generator, blackboard bold font generator',
    type: 'WebApplication'
  },

  // === ENCLOSED STYLES ===
  '/bubble': {
    title: 'Bubble Font Generator - Ⓑⓤⓑⓑⓛⓔ Letters Copy Paste', // 52 chars
    description: 'Free bubble font generator. Create Ⓑⓤⓑⓑⓛⓔ circle letters for Instagram, TikTok, Twitter. Copy paste bubble font generator online free.', // 151 chars
    keywords: 'bubble font generator, circle font generator, enclosed font generator',
    type: 'WebApplication'
  },
  '/bubble-filled': {
    title: 'Bubble Filled Font Generator - 🅑🅤🅑🅑🅛🅔 Solid Fonts', // 52 chars
    description: 'Free filled bubble font generator. Generate solid 🅑🅤🅑🅑🅛🅔 circle fonts for Instagram and TikTok. Copy paste font generator online.', // 149 chars
    keywords: 'filled bubble font generator, solid circle font generator, dark bubble font',
    type: 'WebApplication'
  },
  '/square': {
    title: 'Square Font Generator - 🄱🄾🅇 Letters Copy Paste Free', // 53 chars
    description: 'Free square font generator. Create 🄱🄾🅇🄴🄳 box letter fonts for Instagram bios, TikTok usernames. Copy paste font generator online free.', // 152 chars
    keywords: 'square font generator, box font generator, squared font generator',
    type: 'WebApplication'
  },
  '/square-filled': {
    title: 'Square Filled Font Generator - 🅂🅀🅄🄰🅁🄴 Solid Fonts', // 52 chars
    description: 'Free filled square font generator. Generate solid 🅂🅀🅄🄰🅁🄴 box fonts for high-impact social media. Copy paste font generator online.', // 150 chars
    keywords: 'filled square font generator, solid box font generator, negative squared font',
    type: 'WebApplication'
  },

  // === FULLWIDTH/AESTHETIC ===
  '/fullwidth': {
    title: 'Vaporwave Font Generator - Ｆｕｌｌｗｉｄｔｈ Aesthetic Free', // 55 chars
    description: 'Free vaporwave font generator. Create Ａｅｓｔｈｅｔｉｃ fullwidth fonts for Instagram, TikTok. Copy paste aesthetic font generator online.', // 150 chars
    keywords: 'vaporwave font generator, fullwidth font generator, aesthetic font generator',
    type: 'WebApplication'
  },
  '/spaced': {
    title: 'Spaced Font Generator - S P A C E D Out Letters Free', // 52 chars
    description: 'Free spaced font generator. Add spaces between letters for S P A C E D aesthetic fonts. Copy paste font generator for Instagram, TikTok.', // 153 chars
    keywords: 'spaced font generator, spread out font generator, letter spacing font generator',
    type: 'WebApplication'
  },

  // === DECORATORS ===
  '/strikethrough': {
    title: 'Strikethrough Font Generator - S̶t̶r̶i̶k̶e̶ Copy Paste', // 52 chars
    description: 'Free strikethrough font generator. Generate S̶t̶r̶i̶k̶e̶t̶h̶r̶o̶u̶g̶h̶ crossed out fonts for Instagram, Twitter. Copy paste font generator online.', // 156 chars
    keywords: 'strikethrough font generator, crossed out font generator, copy paste strikethrough',
    type: 'WebApplication'
  },
  '/underline': {
    title: 'Underline Font Generator - U̲n̲d̲e̲r̲l̲i̲n̲e̲ Copy Paste', // 52 chars
    description: 'Free underline font generator. Create U̲n̲d̲e̲r̲l̲i̲n̲e̲d̲ fonts for emphasis on Instagram, Twitter. Copy paste underline font generator online.', // 155 chars
    keywords: 'underline font generator, underlined font copy paste, texto subrayado',
    type: 'WebApplication'
  },
  '/double-underline': {
    title: 'Double Underline Font Generator - D̳o̳u̳b̳l̳e̳ Copy Paste', // 55 chars
    description: 'Free double underline font generator. Generate D̳o̳u̳b̳l̳e̳ underlined fonts for strong emphasis. Copy paste font generator online free.', // 150 chars
    keywords: 'double underline font generator, copy paste double underline, emphasis font',
    type: 'WebApplication'
  },
  '/slash-through': {
    title: 'Slash Through Font Generator - S̷l̷a̷s̷h̷ Copy Paste', // 51 chars
    description: 'Free slash through font generator. Generate S̷l̷a̷s̷h̷e̷d̷ diagonal fonts for Instagram, Twitter. Copy paste font generator online free.', // 151 chars
    keywords: 'slash through font generator, diagonal font generator, copy paste slash',
    type: 'WebApplication'
  },
  '/tilde-strike': {
    title: 'Tilde Strike Font Generator - T̴i̴l̴d̴e̴ Wavy Copy Paste', // 54 chars
    description: 'Free tilde strike font generator. Generate T̴i̴l̴d̴e̴ wavy strikethrough fonts for Instagram, TikTok. Copy paste font generator online.', // 151 chars
    keywords: 'tilde strike font generator, wavy font generator, copy paste tilde',
    type: 'WebApplication'
  },
  '/wavy': {
    title: 'Wavy Text Generator - W̃ãṽỹ Font Squiggly Copy Paste', // 52 chars
    description: 'Free wavy text generator and wavy font generator. Create W̃ãṽỹ squiggly text for Instagram, TikTok. Wavy text copy paste online free.', // 149 chars
    keywords: 'wavy text generator, wavy font generator, wavy text, squiggly text generator, tilde text, wavy font copy and paste',
    type: 'WebApplication'
  },
  '/dotted': {
    title: 'Dotted Font Generator - Ḋọṭṭėḋ Accent Copy Paste', // 49 chars
    description: 'Free dotted font generator. Create Ḋọṭṭėḋ accent fonts for Instagram, Twitter, TikTok. Copy paste dotted font generator online free.', // 150 chars
    keywords: 'dotted font generator, dot accent font generator, copy paste dotted text',
    type: 'WebApplication'
  },

  // === FUN STYLES ===
  '/upside-down': {
    title: 'Upside Down Font Generator - ǝpᴉsd∩ Flip Copy Paste', // 52 chars
    description: 'Free upside down font generator. Flip text to ǝpᴉsd∩ fonts for Instagram, TikTok, Twitter. Copy paste upside down font generator online.', // 153 chars
    keywords: 'upside down font generator, flip font generator, copy paste upside down',
    type: 'WebApplication'
  },
  '/mirror': {
    title: 'Mirror Font Generator - ɿoɿɿiM Backwards Copy Paste', // 52 chars
    description: 'Free mirror font generator. Create ɿoɿɿiM reversed fonts for Instagram, Twitter, TikTok. Copy paste mirror font generator online free.', // 151 chars
    keywords: 'mirror font generator, reversed font generator, copy paste mirror text',
    type: 'WebApplication'
  },
  '/reversed': {
    title: 'Reversed Font Generator - Backwards Fonts Copy Paste', // 52 chars
    description: 'Free reversed font generator. Create desreveR backwards fonts for Instagram, Twitter. Copy paste reverse font generator online free.', // 149 chars
    keywords: 'reverse font generator, backwards font generator, copy paste reversed',
    type: 'WebApplication'
  },
  '/zalgo': {
    title: 'Zalgo Text Generator - Z̷a̸l̷g̶o̵ Glitch Font Copy Paste', // 55 chars
    description: 'Free zalgo text generator. Create Z̷a̸l̷g̶o̵ glitchy cursed text for Discord, TikTok, memes. Zalgo font generator copy paste online free.', // 151 chars
    keywords: 'zalgo, zalgo text generator, zalgo font generator, zalgo font, glitch text generator, cursed text generator, zalgo text',
    type: 'WebApplication'
  },
  '/zalgo-light': {
    title: 'Zalgo Light Font Generator - Subtle Glitch Copy Paste', // 53 chars
    description: 'Free light zalgo font generator. Create subtle Z̈ạl̄ḡo̊ glitch fonts for social media. Copy paste zalgo font generator online free.', // 147 chars
    keywords: 'light zalgo font generator, subtle glitch font, copy paste zalgo',
    type: 'WebApplication'
  },
  '/zalgo-heavy': {
    title: 'Zalgo Heavy Font Generator - Extreme Glitch Copy Paste', // 54 chars
    description: 'Free heavy zalgo font generator. Create extreme Z̷̛̮̈́a̶̯͑l̷͇̀g̸̱͗o̷̜͝ glitch fonts for horror posts. Copy paste font generator online.', // 150 chars
    keywords: 'heavy zalgo font generator, extreme glitch font, copy paste chaos',
    type: 'WebApplication'
  },
  '/ransom': {
    title: 'Ransom Note Generator - 𝐑𝒂𝗻𝘀𝓸𝕞 Letter Font Copy Paste', // 56 chars
    description: 'Free ransom note generator and ransom letter font maker. Create 𝐑𝒂𝗇𝘀𝓸𝕞 mixed fonts for memes, social media. Ransom note font copy paste.', // 153 chars
    keywords: 'ransom note generator, ransom letter generator, ransom font generator, ransom note font, ransom letter font, ransom text generator, ransom note maker',
    type: 'WebApplication'
  },

  // === SCI-FI STYLES ===
  '/runic': {
    title: 'Runic Font Generator - ᚱᚢᚾᛁᚲ Elder Futhark Copy Paste', // 55 chars
    description: 'Free runic font generator. Create ᚱᚢᚾᛁᚲ Elder Futhark Norse runes for Viking, fantasy, gaming content. Copy paste runic fonts online.', // 147 chars
    keywords: 'runic font generator, rune generator, elder futhark, viking runes, norse runes, fantasy font',
    type: 'WebApplication'
  },
  '/alien': {
    title: 'Alien Font Generator - ⴰⵍⵉⵦⵏ Geometric Script Free', // 53 chars
    description: 'Free alien font generator. Create mysterious ⴰⵍⵉⵦⵏ geometric alien text for sci-fi, gaming, Discord. Copy paste alien fonts online.', // 147 chars
    keywords: 'alien font generator, alien text, geometric font, scifi font, extraterrestrial font',
    type: 'WebApplication'
  },
  '/matrix': {
    title: 'Matrix Font Generator - ₥∀┼Я¡× Hacker Style Free', // 50 chars
    description: 'Free matrix font generator. Create ₥∀┼Я¡× hacker-style text for cyberpunk, gaming, Discord. Copy paste matrix fonts online free.', // 144 chars
    keywords: 'matrix font generator, hacker font, leet font, cyberpunk font, coding font',
    type: 'WebApplication'
  },
  '/cyberpunk': {
    title: 'Cyberpunk Font Generator - 【ＣＹＢＥＲ】 Text Copy Paste', // 54 chars
    description: 'Free cyberpunk font generator and cyberpunk text generator. Create 【ＣＹＢＥＲＰＵＮＫ】 neon aesthetic fonts. Futuristic font generator copy paste.', // 156 chars
    keywords: 'cyberpunk font generator, cyberpunk text generator, futuristic font generator, neon font generator, cyber font, futuristic text generator',
    type: 'WebApplication'
  },
  '/glitch-horror': {
    title: 'Glitch Horror Font Generator - Cursed Text Copy Paste', // 53 chars
    description: 'Free glitch horror font generator. Create ɥ̷o̶ɹ̵ɹ̴o̵ɥ upside down cursed text for horror, dark content. Copy paste glitch fonts.', // 146 chars
    keywords: 'glitch horror font, cursed text generator, upside down glitch, horror font, creepy text',
    type: 'WebApplication'
  },

  // === DECORATED STYLES ===
  '/hearts': {
    title: 'Heart Font Generator - ♥Love♥ Fonts Copy Paste Free', // 52 chars
    description: 'Free heart font generator. Add ♥H♥e♥a♥r♥t♥ decorations to your fonts for Instagram, TikTok. Copy paste heart font generator online.', // 150 chars
    keywords: 'heart font generator, love font generator, copy paste heart text',
    type: 'WebApplication'
  },
  '/sparkles': {
    title: 'Sparkle Font - ✨ Sparkle Text Generator Copy Paste Free', // 55 chars
    description: 'Free sparkle font and sparkle text generator. Add ✨sparkles✨ to your text for Instagram, TikTok. Glitter text generator copy paste online.', // 153 chars
    keywords: 'sparkle font, sparkle text generator, sparkles font, glitter text generator, sparkle font generator, sparkles text, sparkle copy and paste',
    type: 'WebApplication'
  },
  '/stars': {
    title: 'Star Font Generator - ★Stars★ Fonts Copy Paste Free', // 52 chars
    description: 'Free star font generator. Decorate fonts with ★S★t★a★r★ symbols for Instagram, TikTok. Copy paste star font generator online free.', // 150 chars
    keywords: 'star font generator, star text generator, copy paste star text',
    type: 'WebApplication'
  },
  '/flowers': {
    title: 'Flower Font Generator - ❀Floral❀ Fonts Copy Paste', // 50 chars
    description: 'Free flower font generator. Add ❀F❀l❀o❀r❀a❀l❀ decorations for Instagram, TikTok. Copy paste flower font generator online free.', // 147 chars
    keywords: 'flower font generator, floral font generator, copy paste flower text',
    type: 'WebApplication'
  },
  '/arrows': {
    title: 'Arrow Font Generator - ➤Arrow➤ Fonts Copy Paste Free', // 53 chars
    description: 'Free arrow font generator. Add ➤A➤r➤r➤o➤w➤ decorations for Instagram, TikTok. Copy paste arrow font generator online free.', // 145 chars
    keywords: 'arrow font generator, arrow text generator, copy paste arrow text',
    type: 'WebApplication'
  },
  '/brackets': {
    title: 'Bracket Font Generator - 【Brackets】 Copy Paste Free', // 53 chars
    description: 'Free bracket font generator. Create 【B】【r】【a】【c】【k】【e】【t】 fonts for Instagram, TikTok. Copy paste bracket font generator.', // 148 chars
    keywords: 'bracket font generator, japanese bracket font, copy paste brackets',
    type: 'WebApplication'
  },
  '/curly': {
    title: 'Curly Bracket Font Generator - 『Fancy』 Copy Paste', // 50 chars
    description: 'Free curly bracket font generator. Create 『C』『u』『r』『l』『y』 fonts for Instagram, TikTok. Copy paste curly font generator online.', // 149 chars
    keywords: 'curly bracket font generator, fancy bracket font, copy paste curly',
    type: 'WebApplication'
  },
  '/parentheses': {
    title: 'Parentheses Font Generator - (Bracketed) Copy Paste', // 51 chars
    description: 'Free parentheses font generator. Create (P)(a)(r)(e)(n) fonts for Instagram, TikTok. Copy paste parentheses font generator online.', // 149 chars
    keywords: 'parentheses font generator, bracketed font generator, copy paste parentheses',
    type: 'WebApplication'
  },

  // === NEW STYLES - December 2024 ===
  '/leet': {
    title: 'Leet Translator - L33t Speak Generator 1337 Copy Paste', // 54 chars
    description: 'Free leet translator and leetspeak generator. Convert text to l33t 1337 hacker style. Leet speak translator for gaming and Discord copy paste.', // 155 chars
    keywords: 'leet translator, leetspeak translator, leet speak generator, l33t generator, 1337 translator, leet speak converter, leet language translator',
    type: 'WebApplication'
  },
  '/random-caps': {
    title: 'Random Caps Generator - RaNdOm CaPs Spongebob Free', // 51 chars
    description: 'Free random caps generator. Create RaNdOm CaPs mocking Spongebob text for memes, sarcasm. Copy paste random caps online free.', // 139 chars
    keywords: 'random caps generator, spongebob text, mocking text generator, sarcastic text, alternating caps',
    type: 'WebApplication'
  },
  '/overline': {
    title: 'Overline Text Generator - O̅v̅e̅r̅l̅i̅n̅e̅ Copy Paste Free', // 55 chars
    description: 'Free overline text generator. Add a line above text for math notation and emphasis. Copy paste overline font online free.', // 136 chars
    keywords: 'overline text generator, line above text, macron text, overline font generator',
    type: 'WebApplication'
  },
  '/morse': {
    title: 'Morse Code Translator - .... . .-.. .-.. --- Copy Paste', // 56 chars
    description: 'Free Morse code translator. Convert text to dots and dashes for secret messages, retro style. Copy paste Morse code online.', // 138 chars
    keywords: 'morse code translator, morse code generator, text to morse, dot dash text, morse converter',
    type: 'WebApplication'
  },
  '/braille': {
    title: 'Braille Text Generator - ⠓⠑⠇⠇⠕ Copy Paste Free', // 49 chars
    description: 'Free Braille text generator. Convert text to Unicode Braille patterns for unique aesthetics. Copy paste Braille text online.', // 138 chars
    keywords: 'braille text generator, braille converter, braille translator, dot pattern text',
    type: 'WebApplication'
  },
  '/fire': {
    title: 'Fire Text Generator - 🔥F🔥I🔥R🔥E🔥 Emoji Copy Paste', // 53 chars
    description: 'Free fire text generator. Add 🔥 fire emojis for hot, trending text on Instagram, TikTok. Copy paste fire text online.', // 135 chars
    keywords: 'fire text generator, flame text, hot text, fire emoji text, lit text generator',
    type: 'WebApplication'
  },
  '/ice': {
    title: 'Ice Text Generator - ❄I❄C❄E❄ Snowflake Copy Paste', // 51 chars
    description: 'Free ice text generator. Add ❄ snowflake symbols for cool, frosty text on social media. Copy paste ice text online free.', // 136 chars
    keywords: 'ice text generator, snowflake text, frozen text, winter text, cold text generator',
    type: 'WebApplication'
  },
  '/lightning': {
    title: 'Lightning Text Generator - ⚡Z⚡A⚡P⚡ Bolt Copy Paste', // 54 chars
    description: 'Free lightning text generator. Add ⚡ bolts for electric, energetic text on social media. Copy paste lightning text online.', // 138 chars
    keywords: 'lightning text generator, bolt text, electric text, power text, energy text generator',
    type: 'WebApplication'
  },
  '/crown': {
    title: 'Crown Text Generator - 👑 King Queen Fonts Copy Paste', // 54 chars
    description: 'Free crown text generator. Add 👑 crowns for royal, boss-like text on Instagram, TikTok. Copy paste crown text online free.', // 138 chars
    keywords: 'crown text generator, king text, queen text, royal text, boss text generator',
    type: 'WebApplication'
  },
  '/skull': {
    title: 'Skull Text Generator - ☠S☠K☠U☠L☠L☠ Dark Copy Paste', // 54 chars
    description: 'Free skull text generator. Add ☠ skulls for dark, edgy text on gaming, Discord. Copy paste skull text online free.', // 130 chars
    keywords: 'skull text generator, death text, dark text generator, edgy text, goth text',
    type: 'WebApplication'
  },
  '/wings': {
    title: 'Wings Text Generator - ༺ Wings ༻ Fancy Copy Paste', // 51 chars
    description: 'Free wings text generator. Add ༺ ༻ decorative wings for elegant text on Instagram, TikTok. Copy paste wings text online.', // 138 chars
    keywords: 'wings text generator, decorative text, fancy brackets, tibetan text, elegant text',
    type: 'WebApplication'
  },
  '/neon': {
    title: 'Neon Text Generator - 》N《》E《》O《》N《 Copy Paste', // 51 chars
    description: 'Free neon text generator. Create 》glowing《 cyberpunk aesthetic text for Instagram, Discord. Copy paste neon text online.', // 137 chars
    keywords: 'neon text generator, cyberpunk text, aesthetic text, vaporwave text, glowing text',
    type: 'WebApplication'
  },

  // === THEMED LANDING PAGES ===
  '/birthday-fonts': {
    title: 'Birthday Font Generator - Happy Birthday Text Copy Paste', // 57 chars
    description: 'Free birthday font generator with party fonts for wishes, invitations, social media. Create happy birthday text. Copy paste birthday fonts.', // 152 chars
    keywords: 'birthday font generator, happy birthday text, birthday text generator, party fonts, birthday fonts',
    type: 'WebApplication'
  },
  '/gaming-fonts': {
    title: 'Gaming Font Generator - Cool Gamer Fonts Copy Paste Free', // 57 chars
    description: 'Free gaming font generator for usernames, clan tags, Discord. Generate cool gamer fonts for PUBG, Fortnite, Free Fire. Copy paste gaming fonts.', // 155 chars
    keywords: 'gaming font generator, gamer fonts, gaming text, esports fonts, clan tag generator, gaming username fonts',
    type: 'WebApplication'
  },
  '/times-new-roman-font': {
    title: 'Times New Roman Font Generator - 𝐓𝐢𝐦𝐞𝐬 Text Copy Paste', // 54cp
    description: 'Free Times New Roman font generator. Turn text into serif 𝐓𝐢𝐦𝐞𝐬 bold, 𝑇𝑖𝑚𝑒𝑠 italic and bold italic letters. Copy paste for Instagram, X, TikTok and Discord.', // 156cp
    keywords: 'times new roman font generator, times new roman font text generator, times new roman copy and paste, times new roman bold, serif font generator',
    type: 'WebApplication'
  },
  '/elegant-fonts': {
    title: 'Elegant Font Generator - 𝓔𝓵𝓮𝓰𝓪𝓷𝓽 Cursive Fonts Copy Paste', // 57cp
    description: 'Free elegant font generator with elegant cursive fonts to copy and paste: 𝓼𝓬𝓻𝓲𝓹𝓽, pretty handwriting and ❀ flower ❀ styles for Instagram bios and names.', // 152cp
    keywords: 'elegant cursive fonts copy and paste, elegant font generator, pretty font generator, handwriting fonts copy and paste, elegant fonts',
    type: 'WebApplication'
  },
  '/twitter-fonts': {
    title: 'Twitter Font Generator - X Fonts 𝗕𝗼𝗹𝗱 𝘐𝘵𝘢𝘭𝘪𝘤 Copy Paste', // 55cp
    description: 'Free Twitter font generator for X. Copy paste 𝗯𝗼𝗹𝗱, 𝘪𝘵𝘢𝘭𝘪𝘤 and 𝓬𝓾𝓻𝓼𝓲𝓿𝓮 fonts for your X display name, bio and posts. See where fonts work and the handle rules.', // 159cp
    keywords: 'twitter fonts, twitter font generator, x fonts, twitter bold text, fonts for twitter, twitter bio fonts',
    type: 'WebApplication'
  },
  '/fantasy-fonts': {
    title: 'Fantasy Text Generator - 𝔐𝔢𝔡𝔦𝔢𝔳𝔞𝔩 & ᚱᚢᚾᛖ Fonts Copy Paste', // 57cp
    description: 'Free fantasy text generator. Copy paste medieval 𝔊𝔬𝔱𝔥𝔦𝔠, ancient ᚱᚢᚾᛖ runes and enchanted script fonts for D&D names, RPG guilds, Discord servers and titles.', // 157cp
    keywords: 'fantasy text generator, fantasy font generator, medieval font copy paste, rune font, d&d name fonts, rpg fonts',
    type: 'WebApplication'
  },
  '/futuristic-font-generator': {
    title: 'Futuristic Font Generator - Cyberpunk Sci-Fi Fonts Free', // 56 chars
    description: 'Free futuristic font generator with cyberpunk, matrix, fullwidth fonts. Generate futuristic text for gaming, Discord. Copy paste futuristic fonts.', // 155 chars
    keywords: 'futuristic font generator, futuristic fonts, cyberpunk font, sci-fi font generator, futuristic text, tech fonts',
    type: 'WebApplication'
  },
  '/leet-translator': {
    title: 'Leet Translator - L33t Sp34k 1337 Generator Copy Paste', // 54 chars
    description: 'Free leet translator and l33t speak generator. Convert text to 1337 hacker style. Leetspeak translator for gaming and Discord. Copy paste leet text.', // 155 chars
    keywords: 'leet translator, leet speak translator, 1337 translator, leetspeak generator, l33t generator, leet text converter',
    type: 'WebApplication'
  },
  '/font-changer': {
    title: 'Font Changer - Change Font Style Online Free Copy Paste', // 55 chars
    description: 'Free font changer to change font style online. Change fonts for Instagram, TikTok, Facebook, Discord. Font style changer copy paste free online.', // 153 chars
    keywords: 'font changer, change fonts style, font style changer, change font online, fonts changer, font changers, online font change',
    type: 'WebApplication'
  },
  '/cool-text': {
    title: 'Cool Text Generator - Cool Text Fonts Copy Paste Free', // 54 chars
    description: 'Free cool text generator with cool text fonts for Instagram, TikTok, Discord, gaming. Generate cool text online. Copy paste cool text fonts free.', // 153 chars
    keywords: 'cool text, cool text generator, cool text fonts, cool text font generator, cool text styles, generate cool text',
    type: 'WebApplication'
  },
  '/font-online': {
    title: 'Fonts Online - Free Font Generator Online Copy Paste', // 53 chars
    description: 'Free fonts online generator. Use fonts online without download for Instagram, TikTok, Discord. Font generator online copy paste. Stylish fonts online.', // 156 chars
    keywords: 'font online, fonts online, online fonts, font generator online, stylish fonts online, fonts online free, text fonts online',
    type: 'WebApplication'
  },
  '/text-styles': {
    title: 'Text Styles Generator - Different Text Styles Copy Paste', // 57 chars
    description: 'Free text styles generator with different text styles for Instagram, TikTok, Discord. Generate text styles online. Copy paste text styles free.', // 151 chars
    keywords: 'text styles, style text, text style, different text styles, text styles generator, font text style, online text styles',
    type: 'WebApplication'
  },
  '/font-keyboard': {
    title: 'Font Keyboard - Keyboard Fonts Style Copy Paste Free', // 53 chars
    description: 'Free font keyboard with keyboard fonts style for Instagram, TikTok, Discord. Type in fancy fonts online. Keyboard font style copy paste free online.', // 155 chars
    keywords: 'font keyboard, keyboard fonts style, keyboard font, cool font keyboard, fancy keyboard, font style keyboard, keyboard fonts',
    type: 'WebApplication'
  },

  // === HIGH-VOLUME KEYWORD PAGES ===
  '/fancy-text': {
    title: 'Fancy Text Generator \u1408 \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1 \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F Copy Paste Free', // 51cp/61u16
    description: 'Free fancy text generator with 60+ styles: \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1, \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F, \uD835\uDD09\uD835\uDD2F\uD835\uDD1E\uD835\uDD28\uD835\uDD31\uD835\uDD32\uD835\uDD2F & more. Instant copy paste for Instagram, TikTok, Discord. Free online.', // 137cp/154u16
    keywords: 'fancy text, fancy fonts, fancy text generator, fancy font, fancy texts, generate fancy text, cool fancy text, fancy font style',
    type: 'WebApplication'
  },
  '/stylish-text-converter': {
    title: 'Stylish Text Converter \u1408 \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1 \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F Copy Paste Free', // 53cp/63u16
    description: 'Free stylish text converter with 60+ styles: \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1, \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F, \uD835\uDD09\uD835\uDD2F\uD835\uDD1E\uD835\uDD28\uD835\uDD31\uD835\uDD32\uD835\uDD2F & more. Convert text to stylish fonts for Instagram, TikTok. Free.', // 134cp/151u16
    keywords: 'stylish text converter, generate stylish text, stylish text, stylish text creator, convert text to stylish',
    type: 'WebApplication'
  },
  '/fancy-letters': {
    title: 'Fancy Letters - 𝓕𝓪𝓷𝓬𝔂 𝓛𝓮𝓽𝓽𝓮𝓻𝓼 Copy and Paste Alphabet', // 57 chars
    description: 'Free fancy letters to copy and paste. Create fancy letter fonts for Instagram, TikTok, Facebook. Fancy letters alphabet and fancy letter generator.', // 158 chars
    keywords: 'fancy letters, fancy letters copy and paste, fancy letter, fancy letters alphabet, fancy letter fonts, fancy letter copy and paste, fancy letters font',
    type: 'WebApplication'
  },
  '/fancy-copy-paste': {
    title: 'Fancy Copy Paste - Fancy Fonts Letters Copy Paste Free', // 55 chars
    description: 'Free fancy fonts and letters to copy and paste. Generate fancy text for Instagram, TikTok, Twitter. Fancy copy paste fonts online free.', // 148 chars
    keywords: 'fancy copy paste, fancy letters copy paste, fancy text copy paste, fancy font copy paste, copy paste fancy fonts',
    type: 'WebApplication'
  },
  '/copy-paste-fonts': {
    title: 'Copy Paste Fonts \u1408 60+ \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1 \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F Fonts Free', // 49cp/59u16
    description: 'Free copy paste fonts with 60+ styles: \uD835\uDDD5\uD835\uDDFC\uD835\uDDF9\uD835\uDDF1, \uD835\uDC9E\uD835\uDCCA\uD835\uDCC7\uD835\uDCC8\uD835\uDCBE\uD835\uDCCB\u212F, \uD835\uDD09\uD835\uDD2F\uD835\uDD1E\uD835\uDD28\uD835\uDD31\uD835\uDD32\uD835\uDD2F & more. Click to copy, paste on Instagram, TikTok, Discord. No signup.', // 132cp/149u16
    keywords: 'copy paste fonts, copy and paste fonts, fonts copy paste, free copy paste fonts, copy paste font generator, letter fonts copy paste',
    type: 'WebApplication'
  }
};

// Indexing policy: default = INDEXABLE.
// All 60 style pages, all 11 language pages, tools, platform pages, blog posts and
// core/legal pages are indexable so crawlers see unique prerendered HTML for each.
// The only pages held out of the index are the thin SEO landing-page clones below:
// near-duplicate homepage variants that cannibalize the homepage and each other and
// earn ~0 organic clicks (see audit). Revisit (consolidate/301 or enrich) later.
const NOINDEX_PATHS = new Set([
  '/fancy-text',
  '/fancy-letters',
  '/cool-fonts',
  '/cool-text',
  '/stylish-text',
  '/stylish-text-converter',
  '/copy-paste-fonts',
  '/fancy-copy-paste',
  '/font-changer',
  '/font-online',
  '/text-styles',
  '/font-keyboard',
  '/facebook-fonts',
]);

// Determine if a path should be noindexed
const shouldNoindex = (path: string): boolean => NOINDEX_PATHS.has(path);

const HREFLANG_MAP: Record<string, string> = {
  '/': 'en',
  '/es': 'es',
  '/pt': 'pt',
  '/fr': 'fr',
  '/id': 'id',
  '/ph': 'fil',
  '/jp': 'ja',
  '/kr': 'ko',
  '/cn': 'zh',
  '/de': 'de',
  '/th': 'th',
  '/vi': 'vi',
};
const LANGUAGE_PATHS = Object.keys(HREFLANG_MAP);

// Generate SEO config for a style page dynamically if not in static config
export const generateStyleSEO = (style: TextStyle | null | undefined): SEOConfigItem => {
  if (!style) return SEO_CONFIG['/'];

  const slug = `/${style.slug}`;
  if (SEO_CONFIG[slug]) return SEO_CONFIG[slug];

  // Fallback dynamic generation
  return {
    title: `${style.name} Text Generator - ${style.example.slice(0, 10)} Free`,
    description: `Create ${style.name.toLowerCase()} text with our free generator. ${style.description} Perfect for Instagram, TikTok, and social media.`,
    keywords: style.keywords.join(', '),
    type: 'WebApplication'
  };
};

interface SEOComponentProps {
  style?: TextStyle | null;
}

function SEO({ style = null }: SEOComponentProps): null {
  const location = useLocation();
  const path = location.pathname;
  // Normalize path: remove trailing slash for config lookup (except for root)
  const normalizedPath = path === '/' ? '/' : path.replace(/\/$/, '');

  // Get config from static config or generate from style
  const config = style ? generateStyleSEO(style) : (SEO_CONFIG[normalizedPath] || SEO_CONFIG['/']);
  // Canonical URLs without trailing slashes (Firebase trailingSlash: false)
  const canonicalUrl = `https://fancymytext.com${path === '/' ? '/' : path.replace(/\/$/, '')}`;

  useEffect(() => {
    // Update document title
    document.title = config.title;

    // Helper to update or create meta tag
    const setMetaTag = (name: string, content: string, isProperty: boolean = false): void => {
      const attr = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Handle noindex for thin pages
    const isNoindex = shouldNoindex(normalizedPath);
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (isNoindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex, nofollow');
    } else {
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }

    // Update meta tags
    setMetaTag('title', config.title);
    setMetaTag('description', config.description);
    setMetaTag('keywords', config.keywords);

    // Open Graph
    setMetaTag('og:title', config.title, true);
    setMetaTag('og:description', config.description, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:type', config.type === 'WebApplication' ? 'website' : 'article', true);

    // Twitter
    setMetaTag('twitter:title', config.title, true);
    setMetaTag('twitter:description', config.description, true);
    setMetaTag('twitter:url', canonicalUrl, true);

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Hreflang tags for language pages
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    if (LANGUAGE_PATHS.includes(normalizedPath)) {
      LANGUAGE_PATHS.forEach(langPath => {
        const link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', HREFLANG_MAP[langPath]);
        link.setAttribute('href', `https://fancymytext.com${langPath}`);
        document.head.appendChild(link);
      });
      const xDefault = document.createElement('link');
      xDefault.setAttribute('rel', 'alternate');
      xDefault.setAttribute('hreflang', 'x-default');
      xDefault.setAttribute('href', 'https://fancymytext.com/');
      document.head.appendChild(xDefault);
    }

    // Update html lang attribute
    document.documentElement.setAttribute('lang', HREFLANG_MAP[normalizedPath] || 'en');

    // Update JSON-LD based on page type
    let jsonLd = document.querySelector('script[data-page-schema]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      jsonLd.setAttribute('data-page-schema', 'true');
      document.head.appendChild(jsonLd);
    }

    interface SchemaData {
      '@context': string;
      '@type': string;
      name: string;
      description: string;
      url: string;
      applicationCategory?: string;
      operatingSystem?: string;
      offers?: {
        '@type': string;
        price: string;
        priceCurrency: string;
      };
      headline?: string;
      image?: string;
      datePublished?: string;
      dateModified?: string;
      author?: { '@type': string; name: string; url?: string };
      publisher?: { '@type': string; name: string; logo: { '@type': string; url: string } };
      mainEntityOfPage?: { '@type': string; '@id': string };
    }

    const schemaData: SchemaData = {
      '@context': 'https://schema.org',
      '@type': config.type,
      'name': config.title,
      'description': config.description,
      'url': canonicalUrl
    };

    if (config.type === 'WebApplication') {
      schemaData.applicationCategory = 'UtilitiesApplication';
      schemaData.operatingSystem = 'Any';
      schemaData.offers = {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      };
    }

    // EEAT fields for blog articles (author, publisher, dates, headline, image).
    if (config.type === 'BlogPosting') {
      const slug = normalizedPath.replace('/blog/', '');
      const post = BLOG_POSTS.find((p) => p.slug === slug);
      schemaData.headline = config.title.split(' - ')[0].split(' | ')[0].split(' ᐈ ')[0];
      schemaData.image = 'https://fancymytext.com/icons/og-image.png';
      schemaData.author = { '@type': 'Organization', name: 'FancyMyText', url: 'https://fancymytext.com/about' };
      schemaData.publisher = {
        '@type': 'Organization',
        name: 'FancyMyText',
        logo: { '@type': 'ImageObject', url: 'https://fancymytext.com/icons/og-image.png' },
      };
      schemaData.mainEntityOfPage = { '@type': 'WebPage', '@id': canonicalUrl };
      if (post) {
        schemaData.datePublished = post.date;
        schemaData.dateModified = post.date;
      }
    }

    jsonLd.textContent = JSON.stringify(schemaData);

    // BreadcrumbList schema
    let breadcrumbSchema = document.querySelector('script[data-breadcrumb-schema]');
    if (normalizedPath === '/') {
      if (breadcrumbSchema) breadcrumbSchema.remove();
    } else {
      if (!breadcrumbSchema) {
        breadcrumbSchema = document.createElement('script');
        breadcrumbSchema.setAttribute('type', 'application/ld+json');
        breadcrumbSchema.setAttribute('data-breadcrumb-schema', 'true');
        document.head.appendChild(breadcrumbSchema);
      }
      const pageName = config.title.split(' - ')[0].split(' | ')[0].split(' \u1408 ')[0];
      const isBlogPost = normalizedPath.startsWith('/blog/') && normalizedPath !== '/blog';
      const items: Array<{ '@type': string; position: number; name: string; item: string }> = [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://fancymytext.com/' }
      ];
      if (isBlogPost) {
        items.push({ '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://fancymytext.com/blog' });
        items.push({ '@type': 'ListItem', position: 3, name: pageName, item: canonicalUrl });
      } else {
        items.push({ '@type': 'ListItem', position: 2, name: pageName, item: canonicalUrl });
      }
      breadcrumbSchema.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items
      });
    }

    // Per-page FAQ schema from DOM
    let faqSchema = document.querySelector('script[data-faq-schema]');
    const faqItems = document.querySelectorAll('.faq-item');
    if (faqItems.length > 0) {
      if (!faqSchema) {
        faqSchema = document.createElement('script');
        faqSchema.setAttribute('type', 'application/ld+json');
        faqSchema.setAttribute('data-faq-schema', 'true');
        document.head.appendChild(faqSchema);
      }
      const faqEntries = Array.from(faqItems).map(item => {
        const question = item.querySelector('summary')?.textContent || '';
        const answer = item.querySelector('p')?.textContent || '';
        return {
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer }
        };
      }).filter(e => e.name && e.acceptedAnswer.text);
      if (faqEntries.length > 0) {
        faqSchema.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqEntries
        });
      } else if (faqSchema) {
        faqSchema.remove();
      }
    } else if (faqSchema) {
      faqSchema.remove();
    }

  }, [path, config, canonicalUrl, normalizedPath]);

  return null;
}

export { SEO_CONFIG };
export default SEO;
